import { List, useTable, DateField } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Row, Col, Tag, Card, Space, Typography, Tooltip } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useMemo } from "react";
import type { Correction, Category } from "@/lib/types";
import { CategoryTag } from "@/components/CategoryTag";
import { StatCard } from "@/components/StatCard";
import { MelBarChart } from "@/components/charts/MelCharts";
import { CATEGORY_LABELS } from "@/lib/constants";
import { categoryColors } from "@/theme/tokens";

const { Text } = Typography;

const CATS = Object.keys(CATEGORY_LABELS) as Category[];

/** Interpola um aqua translúcido pela intensidade (0..1) — heatmap da matriz. */
const heat = (t: number): string =>
  t <= 0 ? "transparent" : `rgba(6, 182, 212, ${0.08 + t * 0.62})`;

/**
 * /admin/corrections — qualidade da classificação a partir de `user_corrections`.
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista as correções paginadas dos
 * fixtures; um `useList({ pagination:{ mode:"off" } })` traz todas para os cards,
 * para a MATRIZ DE CONFUSÃO categoria de→para (grade 5×5 com heatmap) e para a
 * TAXA de erro por categoria. Mesma fonte que GET /admin/corrections na API real
 * (basta VITE_MOCK=false).
 */
export const CorrectionList: React.FC = () => {
  const { tableProps } = useTable<Correction>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
  });

  const { data: allData, isLoading } = useList<Correction>({
    resource: "corrections",
    pagination: { mode: "off" },
  });
  const rows = useMemo(() => allData?.data ?? [], [allData]);

  // ── Matriz de confusão (de × para) + totais por linha/coluna ──
  const matrix = useMemo(() => {
    // grid[de][para] = contagem
    const grid: Record<string, Record<string, number>> = {};
    for (const de of CATS) {
      grid[de] = {};
      for (const para of CATS) grid[de][para] = 0;
    }
    let max = 0;
    let catTotal = 0;
    for (const r of rows) {
      if (r.categoria_de && r.categoria_para && r.categoria_de !== r.categoria_para) {
        grid[r.categoria_de][r.categoria_para] += 1;
        catTotal += 1;
        if (grid[r.categoria_de][r.categoria_para] > max)
          max = grid[r.categoria_de][r.categoria_para];
      }
    }
    // total por categoria de origem (quantas vezes a IA errou classificando como X)
    const rowTotals: Record<string, number> = {};
    for (const de of CATS) rowTotals[de] = CATS.reduce((s, p) => s + grid[de][p], 0);
    return { grid, max: max || 1, catTotal, rowTotals };
  }, [rows]);

  // ── KPIs e ranking de confusões ──
  const stats = useMemo(() => {
    const byField = new Map<string, number>();
    const confusions: { de: Category; para: Category; count: number }[] = [];
    for (const r of rows) {
      const f = r.campo_alterado ?? "—";
      byField.set(f, (byField.get(f) ?? 0) + 1);
    }
    for (const de of CATS)
      for (const para of CATS) {
        const c = matrix.grid[de][para];
        if (c > 0) confusions.push({ de, para, count: c });
      }
    confusions.sort((a, b) => b.count - a.count);
    const topField = [...byField.entries()].sort((a, b) => b[1] - a[1])[0];
    const top = confusions[0];
    return {
      total: rows.length,
      topFieldLabel: topField ? topField[0] : "—",
      topConfusionLabel: top ? `${CATEGORY_LABELS[top.de]} → ${CATEGORY_LABELS[top.para]}` : "—",
      catRate: rows.length ? Math.round((matrix.catTotal / rows.length) * 100) : 0,
      topConfusions: confusions.slice(0, 6).map((c) => ({
        label: `${CATEGORY_LABELS[c.de]} → ${CATEGORY_LABELS[c.para]}`,
        count: c.count,
      })),
    };
  }, [rows, matrix]);

  return (
    <List title="IA · Qualidade da classificação">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Correções (período)"
            value={stats.total}
            loading={isLoading}
            hint={`${stats.catRate}% mudaram a categoria`}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Mudanças de categoria"
            value={matrix.catTotal}
            loading={isLoading}
            hint="erros de classificação"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Campo mais corrigido"
            value={stats.topFieldLabel}
            loading={isLoading}
            hint="onde a IA mais erra"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Confusão top"
            value={stats.topConfusionLabel}
            loading={isLoading}
            hint="categoria de → para"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Matriz de confusão 5×5 (de × para) com heatmap aqua. */}
        <Col xs={24} lg={14}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Matriz de confusão — categoria (de → para)"
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                linha = categoria sugerida · coluna = categoria corrigida
              </Text>
            }
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 460 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 8, textAlign: "left", fontSize: 12, color: "#3A5A60" }}>
                      de \ para
                    </th>
                    {CATS.map((para) => (
                      <th key={para} style={{ padding: 6, textAlign: "center" }}>
                        <CategoryTag value={para} />
                      </th>
                    ))}
                    <th style={{ padding: 6, fontSize: 12, color: "#3A5A60" }}>Σ</th>
                  </tr>
                </thead>
                <tbody>
                  {CATS.map((de) => (
                    <tr key={de}>
                      <th style={{ padding: 6, textAlign: "left" }}>
                        <CategoryTag value={de} />
                      </th>
                      {CATS.map((para) => {
                        const count = matrix.grid[de][para];
                        const isDiag = de === para;
                        return (
                          <td
                            key={para}
                            style={{
                              padding: 0,
                              textAlign: "center",
                              border: "1px solid rgba(45,212,191,0.18)",
                              background: isDiag
                                ? "rgba(16,185,129,0.06)"
                                : heat(count / matrix.max),
                            }}
                          >
                            <Tooltip
                              title={
                                isDiag
                                  ? "Sem confusão (mesma categoria)"
                                  : `${count} correç${count === 1 ? "ão" : "ões"}: ${CATEGORY_LABELS[de]} → ${CATEGORY_LABELS[para]}`
                              }
                            >
                              <div
                                style={{
                                  minWidth: 44,
                                  height: 40,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: count > 0 ? 700 : 400,
                                  color: isDiag ? "#10B981" : count > 0 ? "#0E7490" : "#9CB4B8",
                                }}
                              >
                                {isDiag ? "—" : count || "·"}
                              </div>
                            </Tooltip>
                          </td>
                        );
                      })}
                      <td
                        style={{
                          padding: 6,
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#0E7490",
                        }}
                      >
                        {matrix.rowTotals[de]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>

        {/* Top confusões (ranking horizontal). */}
        <Col xs={24} lg={10}>
          <Card className="mel-glass" bordered={false} title="Top confusões (de → para)">
            <MelBarChart
              data={stats.topConfusions}
              xKey="label"
              series={[{ key: "count", name: "Correções" }]}
              horizontal
              height={300}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mel-glass" bordered={false} title="Correções recentes">
        <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}>
          <Table.Column
            dataIndex="campo_alterado"
            title="Campo"
            render={(v?: string) => <Tag>{v ?? "—"}</Tag>}
          />
          <Table.Column<Correction>
            title="Categoria (de → para)"
            render={(_, row) =>
              row.categoria_de || row.categoria_para ? (
                <Space>
                  <CategoryTag value={row.categoria_de} />
                  <ArrowRightOutlined style={{ color: categoryColors[row.categoria_para ?? ""] ?? "#0E7490" }} />
                  <CategoryTag value={row.categoria_para} />
                </Space>
              ) : (
                "—"
              )
            }
          />
          <Table.Column
            dataIndex="user_id"
            title="Usuário"
            render={(v: string) => <Text code>{v}</Text>}
          />
          <Table.Column
            dataIndex="brain_id"
            title="Brain"
            render={(v: string | null) => (v ? <Text code>{v}</Text> : <Tag>—</Tag>)}
          />
          <Table.Column
            dataIndex="created_at"
            title="Quando"
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY HH:mm" />}
          />
        </Table>
      </Card>
    </List>
  );
};
